import {
  Accordion,
  ActionIcon,
  Alert,
  Badge,
  Box,
  Center,
  Loader,
  Table,
  Text
} from "@mantine/core";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import { InventoryStockResults, Medicine } from "@app/services/drugDelivery/types";

interface InventoryStockViewProps {
  data: InventoryStockResults | null;
  isLoading: boolean;
  selectedMedicine: Medicine | null;
  localQty: Record<number, number>;
  onIncrease: (batchId: number, maxQty: number) => void;
  onDecrease: (batchId: number) => void;
}

export default function InventoryStockView({
  data,
  isLoading,
  selectedMedicine,
  localQty,
  onIncrease,
  onDecrease
}: InventoryStockViewProps) {
  if (isLoading) {
    return (
      <Center py="xl" data-testid="inventorystock-loader">
        <Loader />
      </Center>
    );
  }

  if (data === null) {
    return null;
  }

  const getQty = (batchId: number) => localQty[batchId] ?? 0;

  return (
    <Box data-testid="inventorystock-container">
      <Box mb="sm">
        <Text fw={600} size="md">
          {data.medicine.name}
        </Text>
        <Box className="flex gap-2" mt={4}>
          <Badge variant="light" color="blue">
            CUM: {data.medicine.cum}
          </Badge>
          <Badge variant="light" color="teal">
            INVIMA: {data.medicine.invima}
          </Badge>
        </Box>
      </Box>

      <Accordion data-testid="inventorystock-accordion">
        {data.generics.map((generic) => (
          <Accordion.Item
            key={generic.genericId}
            value={String(generic.genericId)}
            data-testid={`inventorystock-generic-${generic.genericId}`}
          >
            <Accordion.Control>{generic.genericName}</Accordion.Control>
            <Accordion.Panel>
              <Table
                className="biowel-table"
                withTableBorder
                data-testid={`inventorystock-table-${generic.genericId}`}
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th bg="#fff">Nombre comercial</Table.Th>
                    <Table.Th bg="#fff">Lote</Table.Th>
                    <Table.Th bg="#fff" ta="center">
                      Vencimiento
                    </Table.Th>
                    <Table.Th bg="#fff" ta="center">
                      Stock disponible
                    </Table.Th>
                    <Table.Th bg="#fff" ta="center">
                      Cant. a entregar
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {generic.articles.length ? (
                    generic.articles.map((article) => {
                      const qty = getQty(article.batchId);
                      const expirationDate = (article.expirationDate ?? "")
                        .split("T")[0]
                        .replaceAll("/", "-");
                      const exceedsOrder =
                        selectedMedicine?.qtyOrder !== undefined && qty > selectedMedicine.qtyOrder;

                      return (
                        <Table.Tr
                          key={article.batchId}
                          data-testid={`inventorystock-row-${article.batchId}`}
                        >
                          <Table.Td>{article.tradename}</Table.Td>
                          <Table.Td>{article.lotNumber}</Table.Td>
                          <Table.Td ta="center">{expirationDate}</Table.Td>
                          <Table.Td ta="center">{article.onHand}</Table.Td>
                          <Table.Td ta="center">
                            <Box className="flex items-center justify-center gap-1">
                              <ActionIcon
                                size="xs"
                                variant="light"
                                data-testid={`inventorystock-decrement-${article.batchId}`}
                                disabled={qty === 0}
                                onClick={() => onDecrease(article.batchId)}
                              >
                                <IconMinus size={12} />
                              </ActionIcon>
                              <Text size="sm" w={24} ta="center">
                                {qty}
                              </Text>
                              <ActionIcon
                                size="xs"
                                variant="light"
                                data-testid={`inventorystock-increment-${article.batchId}`}
                                disabled={qty === article.onHand}
                                onClick={() => onIncrease(article.batchId, article.onHand)}
                              >
                                <IconPlus size={12} />
                              </ActionIcon>
                            </Box>
                            {exceedsOrder && (
                              <Text size="xs" c="orange" mt={2}>
                                La cantidad seleccionada supera la cantidad ordenada
                              </Text>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })
                  ) : (
                    <Table.Tr>
                      <Table.Td ta="center" colSpan={5}>
                        No hay artículos disponibles.
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>

      {data.generics.length === 0 && (
        <Alert color="yellow" mt="sm">
          No se encontraron genéricos para este medicamento.
        </Alert>
      )}
    </Box>
  );
}
